/**
 * Review Clients API — CRUD for the Supabase review_clients table.
 *
 * The review planner is the Adviser's annual review scheduling surface
 * (CLAUDE.md §34). It is a SEPARATE domain from client files and
 * preparation requests: review planner data must NEVER be counted in
 * file statistics, prep statistics, the bonus counter, or SLA logic.
 * This module therefore imports nothing from filesApi, prepRequestsApi,
 * dashboardStats, bonusCalculator, or slaCalculator — only the shared
 * Supabase client.
 *
 * Phase 1 scope: read, insert (single + bulk import), update planner
 * fields, and delete. `pipeline_task_id` is reserved for Phase 2 and is
 * never written here; `is_test` is reserved and has no UI yet.
 */

import supabase from './supabaseClient';

/**
 * Map a Supabase row (snake_case) to the internal camelCase shape.
 */
function mapRowToReviewClient(row) {
  return {
    id: row.id,
    createdAt: row.created_at || null,
    displayName: row.display_name || '',
    clientSurname: row.client_surname || null,
    clientInitials: row.client_initials || null,
    clientNickname: row.client_nickname || null,
    clientTitle: row.client_title || null,
    isEntity: row.is_entity === true,
    reviewYear: row.review_year,
    reviewMonth: row.review_month,
    status: row.status || 'planned',
    // Phase 2 pipeline timestamps (null until the transition happens).
    submittedAt: row.submitted_at || null,
    appointmentMadeAt: row.appointment_made_at || null,
    completedAt: row.completed_at || null,
    // rolled_from_month / is_overdue / pipeline_task_id are
    // retained-but-unused legacy columns (CLAUDE.md §34.4):
    // review_month is a permanent, Adviser-owned anchor, overdue is
    // derived at render time (isReviewOverdue), and the calendar card
    // is a VIEW of this row — no separate task table exists. Residual
    // values are deliberately not mapped so the app cannot read them.
    s14Applicable: row.s14_applicable === true,
    laIncomeApplicable: row.la_income_applicable === true,
    annualReviewDone: row.annual_review_done === true,
    s14Done: row.s14_done === true,
    laIncomeDone: row.la_income_done === true,
    commissionSplitsDone: row.commission_splits_done === true,
    // Group membership (CLAUDE.md §34.10): members of a group share one
    // review meeting — same month, same status, one calendar card.
    groupId: row.group_id || null,
    // Phase 3 handoff linkage (§34.11): the ONE prep request created for
    // this review meeting, shared by every group member. Null until the
    // Adviser creates it; once set, the create affordance is permanently
    // replaced by the linked state (one meeting, one request, ever).
    prepRequestId: row.prep_request_id || null,
    // Test flag (CLAUDE.md §33): reserved column with no UI yet, mapped so
    // review statistics can exclude test records at source (§21.1 / §37).
    isTest: row.is_test === true,
  };
}

/** Fields a bulk/expanded update may touch. Month moves and status
 *  transitions are the ONLY operations that must propagate across a
 *  whole group (per-member checklist ticks never do). */
function mapSharedPatch(patch = {}) {
  const row = {};
  if ('reviewMonth' in patch) row.review_month = patch.reviewMonth;
  if ('status' in patch) row.status = patch.status;
  if ('submittedAt' in patch) row.submitted_at = patch.submittedAt;
  if ('appointmentMadeAt' in patch) row.appointment_made_at = patch.appointmentMadeAt;
  if ('completedAt' in patch) row.completed_at = patch.completedAt;
  // Handoff linkage (§34.11) — shared across a whole group so every
  // member points at the meeting's ONE prep request.
  if ('prepRequestId' in patch) row.prep_request_id = patch.prepRequestId;
  return row;
}

/**
 * Fetch all review clients for a given year, ordered by month then name.
 */
export async function getReviewClientsForYear(year) {
  const { data, error } = await supabase
    .from('review_clients')
    .select('*')
    .eq('review_year', year)
    .order('review_month', { ascending: true })
    .order('display_name', { ascending: true });

  if (error) throw new Error(`Failed to load review clients: ${error.message}`);
  return (data || []).map(mapRowToReviewClient);
}

/**
 * Create a single review client (the "Add client" modal).
 *
 * @param {object} data
 * @param {string} data.displayName        — free-text client name
 * @param {number} data.reviewYear
 * @param {number} data.reviewMonth        — 2–11 (Jan/Dec excluded by design)
 * @param {boolean} [data.s14Applicable]
 * @param {boolean} [data.laIncomeApplicable]
 */
export async function createReviewClient(data) {
  const row = {
    display_name: data.displayName,
    is_entity: data.isEntity === true,
    review_year: data.reviewYear,
    review_month: data.reviewMonth,
    status: 'planned',
    s14_applicable: data.s14Applicable === true,
    la_income_applicable: data.laIncomeApplicable === true,
  };

  const { data: inserted, error } = await supabase
    .from('review_clients')
    .insert(row)
    .select()
    .single();

  if (error) throw new Error(`Failed to add review client: ${error.message}`);
  return mapRowToReviewClient(inserted);
}

/**
 * Bulk-insert imported review clients (the atwork month-list import).
 * Every row lands as status 'planned' with all checklist flags false —
 * the Adviser toggles S14 / LA applicability per client afterwards.
 *
 * @param {Array} clients — parsed rows from the import preview, each
 *   { displayName, clientSurname, clientInitials, clientNickname,
 *     clientTitle, isEntity }
 * @param {number} year
 * @param {number} month — 2–11
 */
export async function importReviewClients(clients, year, month) {
  const rows = clients.map((c) => ({
    display_name: c.displayName,
    client_surname: c.clientSurname ?? null,
    client_initials: c.clientInitials ?? null,
    client_nickname: c.clientNickname ?? null,
    client_title: c.clientTitle ?? null,
    is_entity: c.isEntity === true,
    review_year: year,
    review_month: month,
    status: 'planned',
  }));

  const { data, error } = await supabase
    .from('review_clients')
    .insert(rows)
    .select();

  if (error) throw new Error(`Failed to import review clients: ${error.message}`);
  return (data || []).map(mapRowToReviewClient);
}

/**
 * GROUP INVARIANT ENFORCEMENT (CLAUDE.md §34.10) — all members of a
 * group always share the same review_month/review_year AND the same
 * status + transition timestamps. This helper is the API-level teeth:
 * given any set of client ids, it expands the set to include EVERY
 * member of every group any of them belongs to, then applies the
 * shared patch (month move / status transition) to the whole expanded
 * set in ONE UPDATE statement, so members can never drift apart.
 *
 * Use this for month moves and status transitions. Per-member fields
 * (checklist ticks, applicability) go through updateReviewClient.
 *
 * @returns {Promise<Array>} the updated rows (camelCase)
 */
export async function updateReviewClientsExpanded(ids, patch = {}) {
  const idList = [...new Set(ids)].filter(Boolean);
  if (idList.length === 0) return [];

  // Expand to full group membership.
  const { data: seedRows, error: seedError } = await supabase
    .from('review_clients')
    .select('id, group_id')
    .in('id', idList);
  if (seedError) throw new Error(`Failed to expand group members: ${seedError.message}`);

  const groupIds = [...new Set((seedRows || []).map((r) => r.group_id).filter(Boolean))];
  const allIds = new Set(idList);
  if (groupIds.length > 0) {
    const { data: memberRows, error: memberError } = await supabase
      .from('review_clients')
      .select('id')
      .in('group_id', groupIds);
    if (memberError) throw new Error(`Failed to expand group members: ${memberError.message}`);
    (memberRows || []).forEach((r) => allIds.add(r.id));
  }

  const row = mapSharedPatch(patch);
  const { data, error } = await supabase
    .from('review_clients')
    .update(row)
    .in('id', [...allIds])
    .select();
  if (error) throw new Error(`Failed to update review clients: ${error.message}`);
  return (data || []).map(mapRowToReviewClient);
}

/**
 * Bulk hard-delete review clients (multi-select Delete — the planner
 * deletion exception, §34.6). Callers are responsible for the pipeline
 * block (no deleting 'submitted'/'appointment_made' rows) and for group
 * cleanup (deleting emptied/undersized groups) — see Reviews.jsx.
 */
export async function deleteReviewClients(ids) {
  const idList = [...new Set(ids)].filter(Boolean);
  if (idList.length === 0) return;
  const { error } = await supabase
    .from('review_clients')
    .delete()
    .in('id', idList);
  if (error) throw new Error(`Failed to delete review clients: ${error.message}`);
}

/**
 * Assign (or clear, with null) the group of a set of clients.
 */
export async function setClientsGroup(ids, groupId) {
  const idList = [...new Set(ids)].filter(Boolean);
  if (idList.length === 0) return [];
  const { data, error } = await supabase
    .from('review_clients')
    .update({ group_id: groupId })
    .in('id', idList)
    .select();
  if (error) throw new Error(`Failed to update group membership: ${error.message}`);
  return (data || []).map(mapRowToReviewClient);
}

// ---- review_groups (one row per review meeting unit; RLS disabled) ----

export async function getReviewGroups() {
  const { data, error } = await supabase.from('review_groups').select('*');
  if (error) throw new Error(`Failed to load review groups: ${error.message}`);
  const labels = {};
  (data || []).forEach((g) => { labels[g.id] = g.label || ''; });
  return labels;
}

export async function createReviewGroup(label) {
  const { data, error } = await supabase
    .from('review_groups')
    .insert({ label })
    .select()
    .single();
  if (error) throw new Error(`Failed to create group: ${error.message}`);
  return { id: data.id, label: data.label || '' };
}

export async function renameReviewGroup(id, label) {
  const { error } = await supabase
    .from('review_groups')
    .update({ label })
    .eq('id', id);
  if (error) throw new Error(`Failed to rename group: ${error.message}`);
}

export async function deleteReviewGroup(id) {
  const { error } = await supabase.from('review_groups').delete().eq('id', id);
  if (error) throw new Error(`Failed to delete group: ${error.message}`);
}

/**
 * Update planner fields on a review client. Accepts a camelCase patch and
 * maps only the recognised fields, so callers can't accidentally touch
 * identity columns or the Phase 2 pipeline_task_id.
 *
 * ENTITY CHECKLIST RULE (§34.10): entities have exactly two checklist
 * items — Annual Review and Commission Splits. S14 / LA Income never
 * apply, so when a patch carries any s14/la field the row's is_entity is
 * checked first and those fields are stripped for entities. The check
 * costs one extra select only when such fields are present.
 */
export async function updateReviewClient(id, patch = {}) {
  let effective = patch;
  const S14_LA_FIELDS = ['s14Applicable', 'laIncomeApplicable', 's14Done', 'laIncomeDone'];
  if (S14_LA_FIELDS.some((f) => f in patch)) {
    const { data: check, error: checkError } = await supabase
      .from('review_clients')
      .select('is_entity')
      .eq('id', id)
      .single();
    if (checkError) throw new Error(`Failed to update review client: ${checkError.message}`);
    if (check?.is_entity === true) {
      effective = { ...patch };
      S14_LA_FIELDS.forEach((f) => delete effective[f]);
    }
  }

  const row = {};
  const patchIn = effective;
  if ('reviewMonth' in patchIn) row.review_month = patchIn.reviewMonth;
  // Name editing (§34.2) — per-client only, never group-expanded (names
  // are individual; mapSharedPatch deliberately excludes these). The
  // caller regenerates display_name from the structured columns.
  if ('displayName' in patchIn) row.display_name = patchIn.displayName;
  if ('clientSurname' in patchIn) row.client_surname = patchIn.clientSurname;
  if ('clientInitials' in patchIn) row.client_initials = patchIn.clientInitials;
  if ('clientNickname' in patchIn) row.client_nickname = patchIn.clientNickname;
  // Phase 2 status flow: planned → submitted → appointment_made →
  // completed, with the matching timestamp set at each transition
  // (null clears nothing here — no backwards transitions exist in the
  // UI). review_month itself is NEVER written by any transition
  // (permanent-anchor principle, §34.4).
  if ('status' in patchIn) row.status = patchIn.status;
  if ('submittedAt' in patchIn) row.submitted_at = patchIn.submittedAt;
  if ('appointmentMadeAt' in patchIn) row.appointment_made_at = patchIn.appointmentMadeAt;
  if ('completedAt' in patchIn) row.completed_at = patchIn.completedAt;
  // rolled_from_month / is_overdue / pipeline_task_id are legacy
  // columns — nothing writes them (§34.4).
  if ('s14Applicable' in patchIn) row.s14_applicable = patchIn.s14Applicable;
  if ('laIncomeApplicable' in patchIn) row.la_income_applicable = patchIn.laIncomeApplicable;
  if ('annualReviewDone' in patchIn) row.annual_review_done = patchIn.annualReviewDone;
  if ('s14Done' in patchIn) row.s14_done = patchIn.s14Done;
  if ('laIncomeDone' in patchIn) row.la_income_done = patchIn.laIncomeDone;
  if ('commissionSplitsDone' in patchIn) row.commission_splits_done = patchIn.commissionSplitsDone;

  const { data, error } = await supabase
    .from('review_clients')
    .update(row)
    .eq('id', id)
    .select()
    .single();

  if (error) throw new Error(`Failed to update review client: ${error.message}`);
  return mapRowToReviewClient(data);
}

/**
 * Fetch every review client that is not yet completed, across all years.
 * One query serves both Phase 2 pipeline surfaces:
 *   - calendar cards = rows with status 'submitted' / 'appointment_made'
 *   - the daily nudge = rows with status 'planned'
 * Completed rows are excluded — a completed review's card leaves the
 * pipeline, and the nudge never counts it.
 */
export async function getActiveReviewClients() {
  const { data, error } = await supabase
    .from('review_clients')
    .select('*')
    .neq('status', 'completed')
    .order('created_at', { ascending: true });

  if (error) throw new Error(`Failed to load active review clients: ${error.message}`);
  return (data || []).map(mapRowToReviewClient);
}

/**
 * Fetch every COMPLETED review client, read-only. Used only by the unified
 * statistics bar's Annual reviews "Completed This Year" tile (CLAUDE.md
 * §37), which getActiveReviewClients cannot serve because it leaves
 * completed rows out. No filtering beyond status: test-record exclusion
 * and the SAST-year check happen in reviewStats.js.
 */
export async function getCompletedReviewClients() {
  const { data, error } = await supabase
    .from('review_clients')
    .select('*')
    .eq('status', 'completed');

  if (error) throw new Error(`Failed to load completed review clients: ${error.message}`);
  return (data || []).map(mapRowToReviewClient);
}

/**
 * Hard-delete a review client ("Remove from planner").
 *
 * Deliberate exception to the "records are never deleted" posture:
 * planner rows are scheduling data, not business records (CLAUDE.md
 * §34.6) — the deletion is Adviser-only and always behind a confirm step.
 */
export async function deleteReviewClient(id) {
  const { error } = await supabase
    .from('review_clients')
    .delete()
    .eq('id', id);

  if (error) throw new Error(`Failed to remove review client: ${error.message}`);
}
